import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  user: null,
  records: [],
  total: 0,
  page: 1,
  limit: 25,
  notifications: [],
  notificationsLoading: true,
  notificationsError: '',
  loading: true,
  error: '',
  selectedRecord: null,
  viewMode: 'records',
  searchTerm: '',
};

export const fetchWfpQueue = createAsyncThunk(
  'paymentRedeemer/fetchWfpQueue',
  async (_, { getState, rejectWithValue }) => {
    const { paymentRedeemer } = getState();
    const userId = paymentRedeemer.user?.userId;
    if (!userId) {
      return rejectWithValue({ message: 'Missing user.' });
    }

    try {
      const res = await api.get('/records', {
        params: {
          page: paymentRedeemer.page,
          limit: paymentRedeemer.limit,
          search: paymentRedeemer.searchTerm.trim() || undefined,
        },
      });

      const { data, total } = res.data || {};
      return { records: data || [], total: total || 0 };
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Failed to fetch records.';
      return rejectWithValue({ status, message });
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  'paymentRedeemer/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/records/notifications');
      return res.data || [];
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to fetch notifications.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchRecordById = createAsyncThunk(
  'paymentRedeemer/fetchRecordById',
  async (recordId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/records/${recordId}`);
      return res.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Could not load the selected record.';
      return rejectWithValue({ message });
    }
  }
);

export const updateRecord = createAsyncThunk(
  'paymentRedeemer/updateRecord',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const {
        assignedCollector,
        assignedPaymentRedeemer,
        comments,
        _id,
        __v,
        ...payload
      } = data;
      const res = await api.put(`/records/${id}`, payload);
      return res.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to update record.';
      return rejectWithValue({ message });
    }
  }
);

const paymentRedeemerSlice = createSlice({
  name: 'paymentRedeemer',
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload || null;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    setSelectedRecord(state, action) {
      state.selectedRecord = action.payload;
    },
    setViewMode(state, action) {
      state.viewMode = action.payload;
    },
    clearError(state) {
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWfpQueue.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchWfpQueue.fulfilled, (state, action) => {
        state.records = action.payload.records;
        state.total = action.payload.total;
        state.loading = false;
      })
      .addCase(fetchWfpQueue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch records.';
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.notificationsLoading = true;
        state.notificationsError = '';
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload;
        state.notificationsLoading = false;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError =
          action.payload?.message || 'Failed to fetch notifications.';
      })
      .addCase(fetchRecordById.fulfilled, (state, action) => {
        state.selectedRecord = action.payload;
      })
      .addCase(fetchRecordById.rejected, (state, action) => {
        state.error = action.payload?.message || 'Could not load the selected record.';
      })
      .addCase(updateRecord.fulfilled, (state, action) => {
        state.selectedRecord = action.payload;
      })
      .addCase(updateRecord.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update record.';
      });
  },
});

export const {
  setUser,
  setPage,
  setLimit,
  setSearchTerm,
  setSelectedRecord,
  setViewMode,
  clearError,
} = paymentRedeemerSlice.actions;

export const selectPaymentRedeemerUser = (state) => state.paymentRedeemer.user;
export const selectPaymentRedeemerRecords = (state) => state.paymentRedeemer.records;
export const selectPaymentRedeemerTotal = (state) => state.paymentRedeemer.total;
export const selectPaymentRedeemerPage = (state) => state.paymentRedeemer.page;
export const selectPaymentRedeemerLimit = (state) => state.paymentRedeemer.limit;
export const selectPaymentRedeemerNotifications = (state) => state.paymentRedeemer.notifications;
export const selectPaymentRedeemerNotificationsLoading = (state) =>
  state.paymentRedeemer.notificationsLoading;
export const selectPaymentRedeemerNotificationsError = (state) =>
  state.paymentRedeemer.notificationsError;
export const selectPaymentRedeemerLoading = (state) => state.paymentRedeemer.loading;
export const selectPaymentRedeemerError = (state) => state.paymentRedeemer.error;
export const selectPaymentRedeemerSelectedRecord = (state) =>
  state.paymentRedeemer.selectedRecord;
export const selectPaymentRedeemerViewMode = (state) => state.paymentRedeemer.viewMode;
export const selectPaymentRedeemerSearchTerm = (state) => state.paymentRedeemer.searchTerm;

export default paymentRedeemerSlice.reducer;
